import * as React from 'react';
import { useMaskedInput } from '@/shared/lib/useMaskedInput';
import { Input, type InputProps } from '../Input';
import { maskPhoneEdit } from './phone-mask';

export interface PhoneInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  value: string;
  onChange: (value: string) => void;
}

/**
 * Поле телефона с маской `+7 (999) 999-99-99`.
 *
 * Буквы в поле не набираются вовсе — маска оставляет только цифры. Это дешевле
 * для человека, чем сообщение об ошибке после отправки: неверный ввод не
 * случается, вместо того чтобы разбираться постфактум.
 *
 * Каретка при правке в середине остаётся на месте (см. useMaskedInput), поэтому
 * поправить одну цифру в набранном номере можно, не перенабирая хвост.
 *
 * Значение остаётся строкой в том виде, в каком его видно, и таким же уезжает
 * в form_data: заявку читают глазами, и `+7 (900) 123-45-67` в ней уместнее
 * голых одиннадцати цифр. Проверка формата на бэке разметку допускает.
 *
 * @use ввод телефона с маской.
 */
export const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onChange, ...rest },
  ref,
) {
  const masked = useMaskedInput({ value, mask: maskPhoneEdit, onChange });
  React.useImperativeHandle(ref, () => masked.ref.current as HTMLInputElement, [masked.ref]);

  return (
    <Input
      ref={masked.ref}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      value={value}
      onChange={masked.onChange}
      {...rest}
    />
  );
});
